<template>
  <Teleport to="body">
    <!-- v-if проверяет оба условия: заставка видима И устройство мобильное -->
    <div 
      v-if="isVisible && isMobile" 
      ref="splashEl"
      class="np-splash" 
      :class="{ 'np-wait': isWaiting, 'np-hide': isHiding }"
      @transitionend="onTransitionEnd"
    >
      <div class="np-splash__dots"></div>
      <div class="np-splash__glow"></div>
      
      <div class="np-splash__center">
        <div class="np-splash__logo">
          <span>N</span><span>e</span><span>x</span><span>P</span><span>l</span><span>a</span><span class="np-blue">n</span>
          <span class="np-splash__dot"></span>
        </div>
        <div class="np-splash__tag">For One · For Team · For Corporates</div>
      </div>
      
      <div class="np-splash__bar"><i></i></div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { useMobile } from '@/composables/useMobile';

const { isMobile } = useMobile();

const props = defineProps({
  isReady: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['hidden']);

const MIN_MS = 2500;
const isVisible = ref(true);
const isWaiting = ref(false);
const isHiding = ref(false);

let waitTimer = null;
let hideTimer = null;
const startTime = Date.now();

const tryHide = () => {
  // Если устройство не мобильное — заставка и так не отрендерена, 
  // но на всякий случай сразу скрываем её из состояния
  if (!isMobile.value) {
    isVisible.value = false;
    emit('hidden');
    return;
  }
  
  if (!props.isReady || !isVisible.value) return;

  const elapsed = Date.now() - startTime;
  const remaining = MIN_MS - elapsed;

  if (remaining > 0) {
    hideTimer = setTimeout(startHideAnimation, remaining);
  } else {
    startHideAnimation();
  }
};

const startHideAnimation = () => {
  if (isHiding.value) return;
  isHiding.value = true;
  
  setTimeout(() => {
    if (isVisible.value) {
      isVisible.value = false;
      emit('hidden');
    }
  }, 500);
};

const onTransitionEnd = (e) => {
  if (e.propertyName === 'opacity' && isHiding.value) {
    isVisible.value = false;
    emit('hidden');
  }
};

onMounted(() => {
  // Запускаем таймеры только если это мобильное устройство
  if (isMobile.value) {
    waitTimer = setTimeout(() => {
      if (!props.isReady) {
        isWaiting.value = true;
      }
    }, MIN_MS);
  }
  
  tryHide();
});

watch(() => props.isReady, (newVal) => {
  if (newVal) tryHide();
});

onBeforeUnmount(() => {
  clearTimeout(waitTimer);
  clearTimeout(hideTimer);
});
</script>

<style>
/* 
  Оставляем стиль без scoped, так как это глобальный оверлей на весь экран.
  Вставьте сюда ваш оригинальный CSS из splash.css
*/
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@600;800&display=swap');

.np-splash{
  position:fixed;inset:0;z-index:9999;overflow:hidden;
  font-family:Montserrat,system-ui,-apple-system,sans-serif;color:#fff;
  background:
  radial-gradient(120vw 80vh at 25% 12%,#3E5A88 0%,rgba(62,90,136,0) 60%),
  radial-gradient(110vw 70vh at 90% 100%,#2A2F66 0%,rgba(42,47,102,0) 60%),
  linear-gradient(180deg,#2F4468 0%,#22365A 55%,#1B2B47 100%);
  transition:opacity .4s ease;
}
.np-splash.np-hide{opacity:0;pointer-events:none}
.np-splash__dots{position:absolute;inset:0;
  background-image:radial-gradient(rgba(255,255,255,.06) 1.5px,transparent 1.6px);background-size:4.2vw 4.2vw;
  -webkit-mask-image:radial-gradient(ellipse at center,#000 30%,transparent 75%);mask-image:radial-gradient(ellipse at center,#000 30%,transparent 75%)}
.np-splash__glow{position:absolute;left:50%;top:50%;width:90vw;height:90vw;margin:-45vw 0 0 -45vw;border-radius:50%;
  background:radial-gradient(closest-side,rgba(108,156,247,.28),transparent);opacity:0;
  animation:np-glow 1.2s ease-out forwards}
.np-splash__center{position:absolute;left:0;right:0;top:50%;transform:translateY(-58%);text-align:center}
.np-splash__logo{font-weight:800;font-size:16.5vw;letter-spacing:-.04em;line-height:1;white-space:nowrap}
.np-splash__logo span{display:inline-block;opacity:0;transform:translateY(60%);filter:blur(8px);
  animation:np-letter .55s cubic-bezier(.215,.61,.355,1) forwards}
.np-splash__logo span:nth-child(1){animation-delay:.15s}
.np-splash__logo span:nth-child(2){animation-delay:.21s}
.np-splash__logo span:nth-child(3){animation-delay:.27s}
.np-splash__logo span:nth-child(4){animation-delay:.33s}
.np-splash__logo span:nth-child(5){animation-delay:.39s}
.np-splash__logo span:nth-child(6){animation-delay:.45s}
.np-splash__logo span:nth-child(7){animation-delay:.51s}
.np-splash__logo .np-blue{color:#6C9CF7}
.np-splash__dot{display:inline-block;width:3.4vw;height:3.4vw;margin-left:.9vw;border-radius:50%;
  background:#6C9CF7;box-shadow:0 0 3vw rgba(108,156,247,.7);opacity:0;transform:translateY(-40vh);
  animation:np-drop .7s linear .7s forwards, np-pulse .5s ease-in-out 1.6s 1}
.np-splash__tag{margin-top:4.2vw;font-size:2.5vw;font-weight:600;letter-spacing:.6em;text-transform:uppercase;white-space:nowrap;color:#B9C6D8;opacity:0;
  animation:np-tag .8s ease-out 1.05s forwards}
.np-splash__bar{position:absolute;left:50%;bottom:14vh;width:22vw;height:.9vw;margin-left:-11vw;border-radius:1vw;background:rgba(255,255,255,.12);overflow:hidden}
.np-splash__bar i{display:block;height:100%;width:0;border-radius:1vw;background:linear-gradient(90deg,#6C9CF7,#9CC0FF);
  animation:np-bar 1.6s ease-in-out .4s forwards}
.np-splash.np-wait .np-splash__bar i{width:40%;animation:np-wait 1.1s ease-in-out infinite}

@keyframes np-glow{to{opacity:1;transform:scale(1.1)}}
@keyframes np-letter{to{opacity:1;transform:translateY(0);filter:blur(0)}}
@keyframes np-tag{to{opacity:1;letter-spacing:.32em}}
@keyframes np-bar{to{width:100%}}
@keyframes np-wait{0%{transform:translateX(-100%)}100%{transform:translateX(250%)}}
@keyframes np-pulse{50%{transform:translateY(0) scale(1.35)}}
@keyframes np-drop{
  0%{opacity:0;transform:translateY(-40vh)}
  10%{opacity:1}
  36%{transform:translateY(0)}
  54%{transform:translateY(-10vh)}
  73%{transform:translateY(0)}
  82%{transform:translateY(-2.5vh)}
  91%{transform:translateY(0)}
  96%{transform:translateY(-.6vh)}
  100%{opacity:1;transform:translateY(0)}
}
@media (prefers-reduced-motion:reduce){
  .np-splash *{animation-duration:.01s!important;animation-delay:0s!important}
}
</style>
